import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CheckIcon, DownloadIcon, FileTextIcon, HeadphonesIcon, SparklesIcon, XIcon } from 'lucide-react';
import { atnaQuestions, consents, invoices, policyDocs } from '../../data/profil';
import type { PanelId } from './AccountNav';
const titles: Record<PanelId, string> = {
  consentements: 'Consents',
  factures: 'My invoices',
  confidentialite: 'Privacy and transparency',
  atna: 'ATNA help'
};
function ConsentsPanel() {
  return <ul className="space-y-3" data-id="element-1685" data-mp="314p7r2fgrebi">
      {consents.map(consent => <li key={consent.id} className="rounded-2xl border border-slate-200 p-5" data-id="element-1686" data-mp="2x1ape541yluw">
          <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-1687" data-mp="2s2zhw118cnfu">
            <h3 className="text-[15px] font-bold text-ville-navy" data-id="element-1688" data-mp="2s525yfito6hx">{consent.title}</h3>
            <span className={`rounded-full px-3 py-1 text-xs font-bold ${consent.status === 'accorde' ? 'bg-ville-indigo-soft text-ville-indigo' : 'bg-amber-50 text-amber-800'}`} data-id="element-1689" data-mp="1lnyl5t4ds187">
              {consent.status === 'accorde' ? 'Granted' : 'To confirm'}
            </span>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-slate-600" data-id="element-1690" data-mp="237ir2jv65ony">{consent.purpose}</p>
          <p className="mt-2 text-xs text-slate-500" data-id="element-1691" data-mp="2862bugy10ueq">{consent.grantedOn}</p>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-1692" data-mp="1thz4b34c5ant">
            <button type="button" className="text-sm font-bold text-ville-indigo underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1693" data-mp="1y97fa3lbd433">
              View details
            </button>
            {consent.withdrawable ? <button type="button" className="text-sm font-semibold text-slate-500 underline underline-offset-4 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1694" data-mp="1aj3dm1roreeq">
                Withdraw my consent
              </button> : <button type="button" className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-4 py-2 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1695" data-mp="3cflyfscat8oo">
                <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1696" data-mp="3w4mndgj6dig1" />
                Confirm
              </button>}
          </div>
        </li>)}
    </ul>;
}
function InvoicesPanel() {
  return <ul className="space-y-3" data-id="element-1697" data-mp="3m7rrl5eadw5g">
      {invoices.map(invoice => <li key={invoice.id} className="rounded-2xl border border-slate-200 p-5" data-id="element-1698" data-mp="2fqwd1s1gpxxy">
          <div className="flex flex-wrap items-start justify-between gap-3" data-id="element-1699" data-mp="2fojq10gukxe1">
            <div className="min-w-0" data-id="element-1700" data-mp="2aqi5sc7oqs0z">
              <p className="font-bold text-ville-navy" data-id="element-1701" data-mp="20o4r8z0t6qh7">{invoice.service}</p>
              <p className="text-sm text-slate-500" data-id="element-1702" data-mp="20ls3u6ca7nwr">
                {invoice.date} · {invoice.reference}
              </p>
            </div>
            <div className="text-right" data-id="element-1703" data-mp="2alsn5sa5i40y">
              <p className="text-lg font-extrabold text-ville-navy" data-id="element-1704" data-mp="2akrd9r4vfnkt">{invoice.amount}</p>
              <span className={`mt-1 inline-block rounded-full px-3 py-1 text-xs font-bold ${invoice.status === 'payee' ? 'bg-slate-100 text-slate-600' : 'bg-ville-indigo-soft text-ville-indigo'}`} data-id="element-1705" data-mp="2fec14qcr316a">
                {invoice.status === 'payee' ? 'Paid' : 'To pay'}
              </span>
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2" data-id="element-1706" data-mp="2yalk9s2iehx5">
            <button type="button" className="text-sm font-bold text-ville-indigo underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1707" data-mp="yz2c3fp15doj">
              View the invoice
            </button>
            <button type="button" className="inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1708" data-mp="2pfwjiolkyffu">
              <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-1709" data-mp="2ygueyihydemt" />
              Download
            </button>
          </div>
        </li>)}
    </ul>;
}
function PrivacyPanel() {
  return <ul className="space-y-3" data-id="element-1710" data-mp="26qpwwqho13or">
      {policyDocs.map(doc => <li key={doc.id} className="rounded-2xl border border-slate-200 p-5" data-id="element-1711" data-mp="2kgtdp1xs056t">
          <div className="flex items-start gap-4" data-id="element-1712" data-mp="2fq38bopco2c4">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-ville-navy" aria-hidden="true" data-id="element-1713" data-mp="2foa1o9eqli44">
              <FileTextIcon className="h-5 w-5" data-id="element-1714" data-mp="1rxw8ugobvf8h" />
            </span>
            <div className="min-w-0" data-id="element-1715" data-mp="1n0mgot9k0pgy">
              <h3 className="text-[15px] font-bold text-ville-navy" data-id="element-1716" data-mp="2j4whfgs0kmu">{doc.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-600" data-id="element-1717" data-mp="2hadi6p2f2le">{doc.description}</p>
              <a href="#profil" className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-ville-indigo hover:underline" data-id="element-1718" data-mp="7by183c8vsyx">
                Read the document
                <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-1719" data-mp="m7496vuwi1uw" />
              </a>
            </div>
          </div>
        </li>)}
    </ul>;
}
function AtnaPanel() {
  return <div className="space-y-6" data-id="element-1720" data-mp="q6d1dwaalroc">
      <div className="flex items-start gap-3 rounded-2xl bg-ville-indigo-soft p-5" data-id="element-1721" data-mp="c9pvv549xt6t">
        <SparklesIcon className="mt-0.5 h-5 w-5 shrink-0 text-ville-indigo" aria-hidden="true" data-id="element-1722" data-mp="h7rghuhcm0ke" />
        <p className="text-sm leading-relaxed text-ville-navy" data-id="element-1723" data-mp="2wzf7zadlf0vt">
          ATNA answers your questions about your account, your data and your services. Its answers are supervised by the Town and never replace a person.
        </p>
      </div>

      <div data-id="element-1724" data-mp="2huuf50yez76j">
        <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500" data-id="element-1725" data-mp="2hvcggbzxoblb">Common questions</h3>
        <ul className="mt-3 space-y-2" data-id="element-1726" data-mp="2dvc0uo13luco">
          {atnaQuestions.map(question => <li key={question} data-id="element-1727" data-mp="292982i45aoxj">
              <button type="button" className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-ville-navy transition-colors duration-150 ease-ville hover:border-ville-indigo hover:text-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1728" data-mp="244qvmhqx5rks">
                {question}
              </button>
            </li>)}
        </ul>
      </div>

      <div className="rounded-2xl border border-slate-200 p-5" data-id="element-1729" data-mp="1l7fyp3srfa8i">
        <div className="flex items-start gap-3" data-id="element-1730" data-mp="3kj8y1730sroe">
          <HeadphonesIcon className="mt-0.5 h-5 w-5 shrink-0 text-ville-navy" aria-hidden="true" data-id="element-1731" data-mp="3kgwif0kqitei" />
          <p className="text-sm leading-relaxed text-slate-600" data-id="element-1732" data-mp="3fhtcldwxkj5h">
            Would you rather speak to a person? A privacy officer can help you Monday to Friday.
          </p>
        </div>
        <button type="button" className="mt-4 inline-flex items-center justify-center rounded-full border border-ville-navy px-5 py-2.5 text-sm font-bold text-ville-navy transition-colors duration-150 ease-ville hover:bg-ville-navy hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-1733" data-mp="3s0nlpjsfku8b">
          Contact an officer
        </button>
      </div>
    </div>;
}
const panels: Record<PanelId, () => JSX.Element> = {
  consentements: ConsentsPanel,
  factures: InvoicesPanel,
  confidentialite: PrivacyPanel,
  atna: AtnaPanel
};
interface AccountPanelProps {
  panel: PanelId | null;
  onClose: () => void;
}
export function AccountPanel({
  panel,
  onClose
}: AccountPanelProps) {
  const Content = panel ? panels[panel] : null;
  return <AnimatePresence data-id="element-1734" data-mp="1jfn18p3g5j2r">
      {panel && Content && <motion.div className="fixed inset-0 z-50 flex justify-end" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: [0.23, 1, 0.32, 1]
    }} data-id="element-1735" data-mp="1jgezy8scbwx9">
          <button type="button" aria-label="Close the panel" onClick={onClose} className="absolute inset-0 bg-ville-navy/40" data-id="element-1736" data-mp="1oezxu1k4e4ij" />
          <motion.aside role="dialog" aria-modal="true" aria-label={titles[panel]} initial={{
        x: 40,
        opacity: 0
      }} animate={{
        x: 0,
        opacity: 1
      }} exit={{
        x: 40,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.23, 1, 0.32, 1]
      }} className="relative flex h-full w-full max-w-xl flex-col overflow-y-auto bg-white shadow-2xl" data-id="element-1737" data-mp="1x71x3wigder8">
            <header className="sticky top-0 flex items-center justify-between gap-4 border-b border-slate-200 bg-white px-6 py-5" data-id="element-1738" data-mp="crymedgf2j9m">
              <h2 className="text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-1739" data-mp="2ofbunbq0hj6l">{titles[panel]}</h2>
              <button type="button" onClick={onClose} aria-label="Close" className="grid h-10 w-10 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-1740" data-mp="2tctso72hqdzt">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-1741" data-mp="29qfgcwxd39xj" />
              </button>
            </header>
            <div className="px-6 py-6" data-id="element-1742" data-mp="1vrfozhigsgxo">
              <Content data-id="element-1743" data-mp="20oxwr4kqwq43" />
            </div>
          </motion.aside>
        </motion.div>}
    </AnimatePresence>;
}